import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  students,
  performance,
  course,
  courses,
  subjects,
  chapters,
  topics,
  coursesContent,
  chapter,
  topic,
  quiz,
  questions,
  courseid,
  submit,
  JWTToken,
} from "./constant/AppConstant";
import { getAuthToken } from "./App";
import { setUserToken } from "./components/User/userSlice";

export const baseUrl =
  "https://nexo-dev-fbf0cvc7bhehbuft.australiacentral-01.azurewebsites.net/";

// Custom fetch base query with offline support
const customFetchBaseQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  prepareHeaders: async (headers, { getState, dispatch }) => {
    let token = getState().user?.userToken;

    if (getAuthToken) {
      try {
        const freshToken = await getAuthToken();
        if (freshToken && freshToken !== token) {
          token = freshToken;
          dispatch(setUserToken(freshToken));
          const storedTokenStr = localStorage.getItem(JWTToken);
          if (storedTokenStr) {
            const tokenData = JSON.parse(storedTokenStr);
            tokenData.accessToken = freshToken;
            localStorage.setItem(JWTToken, JSON.stringify(tokenData));
          }
        }
      } catch (e) {
        console.warn("Could not silently get a fresh token:", e);
      }
    }

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const liveKitBaseQuery = fetchBaseQuery({
  baseUrl:
    "https://learnido-livekit-bdbbh2gdfdh2g2ah.australiacentral-01.azurewebsites.net/api/v1/",
  prepareHeaders: async (headers, { getState, dispatch }) => {
    let token = getState().user?.userToken;

    if (getAuthToken) {
      try {
        const freshToken = await getAuthToken();
        if (freshToken && freshToken !== token) {
          token = freshToken;
          dispatch(setUserToken(freshToken));
          const storedTokenStr = localStorage.getItem(JWTToken);
          if (storedTokenStr) {
            const tokenData = JSON.parse(storedTokenStr);
            tokenData.accessToken = freshToken;
            localStorage.setItem(JWTToken, JSON.stringify(tokenData));
          }
        }
      } catch (e) {
        console.warn("Could not silently get a fresh token:", e);
      }
    }

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// Enhanced base query with offline support
const baseQueryWithOfflineSupport = async (args, api, extraOptions) => {
  console.log("API Request:", { url: args.url, method: args.method });
  try {
    // Attempt online request first
    const result = await customFetchBaseQuery(args, api, extraOptions);
    console.log("API Response:", {
      url: args.url,
      status: result.error ? "error" : "success",
    });

    // If the request was successful, cache it
    if (result.data) {
      try {
        const cache = await caches.open("learnido-api-v1");
        const request = new Request(baseUrl + args.url, {
          method: args.method || "GET",
          headers: args.headers || {},
          body: args.body ? JSON.stringify(args.body) : undefined,
        });
        const response = new Response(JSON.stringify(result.data), {
          headers: { "Content-Type": "application/json" },
        });
        await cache.put(request, response);
      } catch (cacheError) {
        console.warn("Failed to cache API response:", cacheError);
      }
    }

    return result;
  } catch (error) {
    if (
      args.method === "POST" ||
      args.method === "PUT" ||
      args.method === "DELETE"
    ) {
      throw error;
    }
    // If offline or other request fails, try to get from cache
    try {
      const cache = await caches.open("learnido-api-v1");
      const cachedRequest = new Request(baseUrl + args.url);
      const cachedResponse = await cache.match(cachedRequest);

      if (cachedResponse) {
        const data = await cachedResponse.json();
        return { data };
      }

      // If no cached data, return error with offline flag
      return {
        error: {
          status: "OFFLINE",
          data: { message: "You are offline and no cached data is available" },
        },
      };
    } catch (cacheError) {
      // If cache access fails, return original error
      return {
        error: {
          status: error.status,
          data: error.data,
        },
      };
    }
  }
};

const APIConnect = createApi({
  reducerPath: "API",
  baseQuery: baseQueryWithOfflineSupport,
  tagTypes: ["Sprint", "Subtopic"],
  endpoints: (builder) => ({
    getTopics: builder.query({
      query: () => ({
        url: "Topics",
        method: "GET",
      }),
    }),
    // ... existing endpoints remain the same ...
    getClassroomAnalytics: builder.query({
      query: (params) => ({
        url: `/Analytic/classroom/${params.classroomId}`,
        method: "GET",
      }),
    }),
    getSchools: builder.query({
      query: () => ({
        url: "Schools",
        method: "GET",
      }),
    }),

    getStudentLearningIndices: builder.query({
      query: (studentId) => ({
        url: `Analytic/indices/${studentId}`,
        method: "GET",
      }),
    }),
    getStudentLearningIndicesProgress: builder.query({
      query: ({ studentId, startDate, endDate }) => ({
        url: `Analytic/indices/${studentId}/progress?startDate=${startDate}&endDate=${endDate}`,
        method: "GET",
      }),
    }),
    getStudentSkillTagAnalytics: builder.query({
      query: ({ studentId, startDate, endDate }) => ({
        url: `Analytic/skills/${studentId}?startDate=${startDate}&endDate=${endDate}`,
        method: "GET",
      }),
    }),
    getStudentAssessmentPerformance: builder.query({
      query: ({ studentId, courseId }) => ({
        url: `/Analytic/assessmentPerformance/student/${studentId}/course/${courseId}`,
        method: "GET",
      }),
    }),

    getSchoolById: builder.query({
      query: (id) => ({
        url: `Schools/${id}`,
        method: "GET",
      }),
    }),

    createSchool: builder.mutation({
      query: (params) => ({
        url: "Schools",
        method: "POST",
        body: params,
      }),
    }),

    updateSchool: builder.mutation({
      query: (params) => ({
        url: `Schools/${params.id}`,
        method: "PUT",
        body: params,
      }),
    }),

    deleteSchool: builder.mutation({
      query: (params) => ({
        url: `Schools?Id=${params.Id}`,
        method: "DELETE",
      }),
    }),
    // Add content management endpoints
    getAllContent: builder.query({
      query: () => ({
        url: "CoursesContent",
        method: "GET",
      }),
    }),

    getStudentDashboard: builder.query({
      query: (studentId) => ({
        url: `Students/dashboard`,
        method: "GET",
      }),
    }),

    getContentById: builder.query({
      query: (id) => {
        if (typeof id === "string") {
          if (id.startsWith("assignment_")) {
            return {
              url: `CoursesContent/assignments/${id.replace("assignment_", "")}`,
              method: "GET",
            };
          }
          const baseId = id.replace("quiz_", "").replace("doc_", "");
          return {
            url: `CoursesContent/${baseId}`,
            method: "GET",
          };
        }
        return {
          url: `CoursesContent/${id}`,
          method: "GET",
        };
      },
      providesTags: (result, error, id) => [{ type: "Subtopic", id }],
    }),

    getContentByTopicId: builder.query({
      query: (topicId) => ({
        url: `CoursesContent/topic/${topicId}`,
        method: "GET",
      }),
    }),

    createContent: builder.mutation({
      query: (params) => ({
        url: "CoursesContent",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["Subtopic"],
    }),

    updateContent: builder.mutation({
      query: (params) => ({
        url: `CoursesContent/${params.id}`,
        method: "PUT",
        body: params,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Subtopic", id }],
    }),

    deleteContent: builder.mutation({
      query: (id) => ({
        url: `CoursesContent?id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Subtopic", id }],
    }),

    // Add notification endpoints
    getCourseById: builder.query({
      query: (id) => ({
        url: `Courses/${id}`,
        method: "GET",
      }),
    }),
    getCourseProgress: builder.query({
      query: ({ courseId, classroomId }) => ({
        url: `Courses/${courseId}/progress?classroomId=${classroomId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 300, // cache for 5 minutes
    }),

    getAdminCourses: builder.query({
      query: () => ({
        url: "Courses",
        method: "GET",
      }),
    }),

    createCourse: builder.mutation({
      query: (params) => ({
        url: "Courses",
        method: "POST",
        body: params,
      }),
    }),

    updateCourse: builder.mutation({
      query: (params) => ({
        url: `Courses/${params.id}`,
        method: "PUT",
        body: params,
      }),
    }),

    deleteCourse: builder.mutation({
      query: (params) => ({
        url: `Courses?id=${params.id}`,
        method: "DELETE",
      }),
    }),

    assignInstructor: builder.mutation({
      query: ({ courseId, instructorIds }) => ({
        url: `Courses/${courseId}/assign-instructor`,
        method: "POST",
        body: instructorIds,
      }),
    }),

    assignExpert: builder.mutation({
      query: ({ courseId, expertIds }) => ({
        url: `Courses/${courseId}/assign-expert`,
        method: "POST",
        body: expertIds,
      }),
    }),

    getSubjects: builder.query({
      query: () => ({
        url: "Subjects",
        method: "GET",
      }),
    }),
    getClassroomNotifications: builder.query({
      query: ({ classroomId }) => ({
        url: `/Notifications/classroomNotification/${classroomId}`,
        method: "GET",
      }),
    }),
    getSchoolNotifications: builder.query({
      query: (schoolId) => ({
        url: `/Notifications/schoolNotification/${schoolId}`,
        method: "GET",
      }),
    }),
    getStudentNotifications: builder.query({
      query: ({ page = 1, size = 20 } = {}) => ({
        url: `/Notifications/studentNotifications?Page=${page}&Size=${size}`,
        method: "GET",
      }),
    }),
    createSchoolNotification: builder.mutation({
      query: ({ SchoolId, NotifyString }) => ({
        url: `/Notifications/schoolNotification`,
        method: "POST",
        body: { SchoolId, NotifyString },
      }),
    }),
    createClassroomNotification: builder.mutation({
      query: ({ ClassroomId, NotifyString }) => ({
        url: `/Notifications/classroomNotification`,
        method: "POST",
        body: { ClassroomId, NotifyString },
      }),
    }),
    updateStudentNotification: builder.mutation({
      query: ({ id, IsRead }) => ({
        url: `/Notifications/studentNotifications/${id}`,
        method: "PUT",
        body: { IsRead },
      }),
    }),
    // Add new Q&A endpoints
    getQueries: builder.query({
      query: (params) => ({
        url: params.classroomId
          ? `QueryReply?classroomId=${params.classroomId}`
          : "QueryReply",
        method: "GET",
      }),
    }),
    createQuery: builder.mutation({
      query: (params) => ({
        url: "QueryReply",
        method: "POST",
        body: {
          classroomId: params.classroomId,
          queryText: params.queryText,
          studentId: params.studentId,
          queryType: params.queryType,
        },
      }),
    }),
    updateQuery: builder.mutation({
      query: ({ queryId, queryText }) => ({
        url: `QueryReply/${queryId}`,
        method: "PUT",
        body: {
          queryText,
        },
      }),
    }),
    updateReply: builder.mutation({
      query: ({ queryId, answerText }) => ({
        url: `QueryReply/reply/${queryId}`,
        method: "PUT",
        body: {
          answerText,
        },
      }),
    }),

    getTestData: builder.query({
      query: () => ({
        url: "todos",
      }),
    }),
    getBookmarks: builder.query({
      query: (params) => ({
        url: `Bookmark`,
        method: "GET",
      }),
    }),
    createBookmark: builder.mutation({
      query: (params) => {
        const body = {
          topicId: params.topicId,
        };

        // Only include bookmarkCollectionId if it's provided
        if (params.bookmarkCollectionId) {
          body.bookmarkCollectionId = params.bookmarkCollectionId;
        }

        return {
          url: `Bookmark`,
          method: "POST",
          body: body,
        };
      },
    }),
    deleteBookmark: builder.mutation({
      query: (params) => ({
        url: `Bookmark/${params.id}`,
        method: "DELETE",
      }),
    }),

    // Submissions API endpoints
    getSubmissionById: builder.query({
      query: (id) => ({
        url: `Submissions/${id}`,
        method: "GET",
      }),
    }),

    getStudentSubmission: builder.query({
      query: ({ classroomId, assignmentId }) => ({
        url: `Submissions/studentSubmission?classroomId=${classroomId}&assignmentId=${assignmentId}`,
        method: "GET",
      }),
    }),

    // Instructor: get all submissions in a class (no assignment filter)
    getAllClassSubmissions: builder.query({
      query: ({ classroomId }) => ({
        url: `Submissions/classSubmissions?classroomId=${classroomId}`,
        method: "GET",
      }),
    }),

    // Instructor: get all submissions in a class for an assignment (assignmentId = subtopicId)
    getClassSubmissions: builder.query({
      query: ({ classroomId, assignmentId }) => ({
        url: `Submissions/classSubmissions?classroomId=${classroomId}&assignmentId=${assignmentId}`,
        method: "GET",
      }),
    }),

    getCourseAssignmentsSubmissions: builder.query({
      query: ({ courseId, classroomId, assignmentTagId, submissionStatus }) => {
        let url = `Courses/${courseId}/assignments-submissions?classroomId=${classroomId}`;
        if (assignmentTagId) url += `&assignmentTagId=${assignmentTagId}`;
        if (submissionStatus) url += `&submissionStatus=${submissionStatus}`;
        return {
          url,
          method: "GET",
        };
      },
    }),

    createSubmission: builder.mutation({
      query: (params) => ({
        url: `Submissions`,
        method: "POST",
        body: {
          classroomId: params.classroomId,
          assignmentId: params.assignmentId,
          fileUrl: params.fileUrl || "",
          additionalFiles: params.additionalFiles || [],
          selfReflectiveAnswers: params.selfReflectiveAnswers || [],
        },
      }),
    }),

    updateSubmission: builder.mutation({
      query: (params) => ({
        url: `Submissions`,
        method: "PUT",
        body: {
          classroomId: params.classroomId,
          assignmentId: params.assignmentId,
          fileUrl: params.fileUrl || "",
          additionalFiles: params.additionalFiles || [],
          selfReflectiveAnswers: params.selfReflectiveAnswers || [],
        },
      }),
    }),

    // Instructor scoring for class submissions
    scoreClassSubmission: builder.mutation({
      query: ({
        classroomId,
        topicId,
        assignmentId,
        studentId,
        score,
        instructorFeedback,
        aiFeedback,
      }) => ({
        url: `Submissions/scoring`,
        method: "PUT",
        body: {
          classroomId,
          topicId,
          assignmentId,
          studentId,
          score,
          instructorFeedback,
          aiFeedback,
        },
      }),
    }),

    evaluateSubmissionAI: builder.mutation({
      queryFn: async (
        { fileUrl, studentId },
        _api,
        _extraOptions,
        _baseQuery,
      ) => {
        try {
          const BASE_URL = "https://vyudiai.stmin.dev/api";
          const ASSESSMENT_ID = "e3d7ff49-fc49-40a6-9c1e-33375e7b7f1f";

          // 1. Download file from frontend
          const fileRes = await fetch(fileUrl);
          if (!fileRes.ok) throw new Error("Failed to download file");

          const contentType =
            fileRes.headers.get("Content-Type") || "application/octet-stream";
          const arrayBuffer = await fileRes.arrayBuffer();
          const blob = new Blob([arrayBuffer], { type: contentType });

          let fileName = "submission-file";
          try {
            const urlObj = new URL(fileUrl, "http://localhost"); // Base for relative URLs
            fileName = urlObj.pathname.split("/").pop() || "submission-file";
          } catch (e) {
            fileName =
              fileUrl.split("/").pop().split("?")[0] || "submission-file";
          }

          // 2. Create Submission Record
          const createRes = await fetch(`${BASE_URL}/submissions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              assessmentId: ASSESSMENT_ID,
              studentId: studentId,
            }),
          });

          if (!createRes.ok) {
            const errText = await createRes.text();
            throw new Error(
              `Failed to create submission: ${createRes.status} ${errText}`,
            );
          }
          const submissionData = await createRes.json();
          // Assuming response structure ApiResponse<Submission> where data is in .data or directly returned?
          // Documentation says Response: ApiResponse<Submission>. Usually means { success: true, data: { id: ... } } or similar.
          // Let's assume standard response structure. If usage fails, we might need to inspect.
          const submissionId = submissionData.data?.id || submissionData.id;

          if (!submissionId) throw new Error("No submission ID returned");

          // 3. Upload File
          const formData = new FormData();
          formData.append("files", blob, fileName); // Note: Doc says key 'files'

          const uploadRes = await fetch(
            `${BASE_URL}/submissions/${submissionId}/upload`,
            {
              method: "POST",
              body: formData,
            },
          );

          if (!uploadRes.ok) {
            const errText = await uploadRes.text();
            throw new Error(
              `Failed to upload file: ${uploadRes.status} ${errText}`,
            );
          }

          // 4. Trigger Evaluation
          const evalRes = await fetch(
            `${BASE_URL}/submissions/${submissionId}/evaluations`,
            {
              method: "POST",
            },
          );

          if (!evalRes.ok) {
            const errText = await evalRes.text();
            throw new Error(
              `Failed to start evaluation: ${evalRes.status} ${errText}`,
            );
          }
          const evalData = await evalRes.json();

          // return result as string for the textarea
          return { data: JSON.stringify(evalData, null, 2) };
        } catch (error) {
          console.error("AI Eval Error:", error);
          return { error: { status: "CUSTOM_ERROR", error: error.message } };
        }
      },
    }),

    checkPlagiarism: builder.mutation({
      queryFn: async (
        { fileUrl, studentId, submissionId, searchEngine = "jina" },
        _api,
        _extraOptions,
        _baseQuery,
      ) => {
        try {
          const BASE_URL = "https://vyudiai.stmin.dev/api";

          // 1. Download file from frontend
          const fileRes = await fetch(fileUrl);
          if (!fileRes.ok) throw new Error("Failed to download file");

          const contentType =
            fileRes.headers.get("Content-Type") || "application/octet-stream";
          const arrayBuffer = await fileRes.arrayBuffer();
          const blob = new Blob([arrayBuffer], { type: contentType });

          let fileName = "submission-file";
          try {
            const urlObj = new URL(fileUrl, "http://localhost");
            fileName = urlObj.pathname.split("/").pop() || "submission-file";
          } catch (e) {
            fileName =
              fileUrl.split("/").pop().split("?")[0] || "submission-file";
          }

          // 2. Upload to Plagiarism Check
          const formData = new FormData();
          formData.append("file", blob, fileName);
          formData.append("studentId", studentId || "");
          formData.append("referenceId", submissionId || "");
          formData.append("searchEngine", searchEngine);

          const response = await fetch(`${BASE_URL}/plagiarism/check`, {
            method: "POST",
            body: formData,
          });

          if (!response.ok) {
            const errText = await response.text();
            throw new Error(
              `Failed to start plagiarism check: ${response.status} ${errText}`,
            );
          }

          const result = await response.json();
          // The API returns 202 with { success: true, data: { id: "..." } }
          return { data: result.data };
        } catch (error) {
          console.error("Plagiarism Check Error:", error);
          return { error: { status: "CUSTOM_ERROR", error: error.message } };
        }
      },
    }),

    getPlagiarismResult: builder.query({
      query: (id) => ({
        url: `https://vyudiai.stmin.dev/api/plagiarism/${id}`,
        method: "GET",
      }),
    }),

    getBookmarkCollection: builder.query({
      query: (params) => ({
        url: `BookmarkCollection`,
        method: "GET",
      }),
    }),
    createBookmarkCollection: builder.mutation({
      query: (params) => ({
        url: `BookmarkCollection`,
        method: "POST",
        body: {
          bookmarkCollectionName: params.name,
        },
      }),
    }),
    editBookmarkCollection: builder.mutation({
      query: (params) => ({
        url: `BookmarkCollection/${params.id}`,
        method: "PUT",
        body: {
          bookmarkCollectionName: params.name,
        },
      }),
    }),
    deleteBookmarkCollection: builder.mutation({
      query: (id) => ({
        url: `BookmarkCollection/${id}`,
        method: "DELETE",
      }),
    }),
    // Student Note API endpoints (updated for new structure)
    getStudentAllNotes: builder.query({
      query: () => ({
        url: `studentnote/student`,
        method: "GET",
      }),
    }),
    getStudentNoteById: builder.query({
      query: (id) => ({
        url: `studentnote/${id}`,
        method: "GET",
      }),
    }),
    getStudentNotesByCourse: builder.query({
      query: (courseId) => ({
        url: `studentnote/course/${courseId}`,
        method: "GET",
      }),
    }),
    getStudentNotesByTopic: builder.query({
      query: (topicId) => ({
        url: `studentnote/topic/${topicId}`,
        method: "GET",
      }),
    }),
    createNewStudentNote: builder.mutation({
      query: (params) => ({
        url: `studentnote`,
        method: "POST",
        body: params,
      }),
    }),
    updateStudentNote: builder.mutation({
      query: (params) => ({
        url: `studentnote/${params.id}`,
        method: "PUT",
        body: { ...params, id: undefined },
      }),
    }),
    deleteStudentNote: builder.mutation({
      query: (id) => ({
        url: `studentnote/${id}`,
        method: "DELETE",
      }),
    }),
    // Subtopic endpoints
    getSubtopics: builder.query({
      query: (topicId) => ({
        url: `CoursesContent/topic/${topicId}`,
        method: "GET",
      }),
    }),

    getSubtopicById: builder.query({
      query: (id) => {
        const baseId =
          typeof id === "string"
            ? id
              .replace("assignment_", "")
              .replace("quiz_", "")
              .replace("doc_", "")
            : id;
        return {
          url: `CoursesContent/subtopicId/${baseId}`,
          method: "GET",
        };
      },
      providesTags: (result, error, id) => [{ type: "Subtopic", id }],
    }),

    createSubtopic: builder.mutation({
      query: (params) => ({
        url: "CoursesContent",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["Subtopic"],
    }),

    updateSubtopic: builder.mutation({
      query: (params) => ({
        url: `CoursesContent/${params.Id || params.id}`,
        method: "PUT",
        body: params,
      }),
      invalidatesTags: (result, error, params) => [
        { type: "Subtopic", id: params.Id || params.id },
      ],
    }),

    deleteSubtopic: builder.mutation({
      query: (id) => ({
        url: `CoursesContent?id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Subtopic", id }],
    }),

    updateSubtopicWeights: builder.mutation({
      query: ({ topicId, weights }) => ({
        url: `CoursesContent/topic/${topicId}/weight`,
        method: "PUT",
        body: weights,
      }),
      invalidatesTags: ["Subtopic"],
    }),

    reorderSubtopics: builder.mutation({
      query: ({ topicId, subTopicIds }) => ({
        url: `CoursesContent/topic/${topicId}/reorder`,
        method: "PUT",
        body: { SubTopicIds: subTopicIds },
      }),
      invalidatesTags: ["Subtopic"],
    }),

    updateAssignmentWeights: builder.mutation({
      query: ({ subtopicId, weights }) => ({
        url: `CoursesContent/${subtopicId}/weight`,
        method: "PUT",
        body: { weights },
      }),
    }),

    // SCORM file upload endpoint
    uploadScormFile: builder.mutation({
      query: (formData) => ({
        url: "UploadScormFile",
        method: "POST",
        body: formData,
        formData: true,
      }),
    }),

    // (moved recording + egress endpoints to liveKitApi)

    getAssignmentById: builder.query({
      query: (id) => ({
        url: `CoursesContent/assignments/${id}`,
        method: "GET",
      }),
    }),

    getUserById: builder.query({
      query: (id) => ({
        url: `Users/${id}`,
        method: "GET",
      }),
    }),

    getQuizQuestions: builder.query({
      query: (quizId) => ({
        url: `Quizzes/${quizId}`,
      }),
    }),

    getSubtopicContent: builder.query({
      query: (subtopicId) => ({
        url: `CoursesContent/${subtopicId}`,
      }),
    }),

    getQuestionById: builder.query({
      query: (questionId) => ({
        url: `Questions/${questionId}`,
      }),
    }),
    getStudentPerformanceData: builder.query({
      query: (id) => ({
        url: `${students}/${id}/${performance}`,
      }),
    }),
    getTopicQuizPerformance: builder.query({
      query: (id) => ({
        url: `Students/performance/topic/${id}`,
      }),
    }),
    getAllCourseData: builder.query({
      query: (id) => ({
        url: `${students}/${course}`,
      }),
    }),
    getCourseDetailsData: builder.query({
      query: (obj) => ({
        url: `${courses}/${obj.courseId}`,
      }),
    }),
    getAllSubjectsData: builder.query({
      query: () => ({
        url: `${subjects}`,
      }),
    }),
    getAllCoursesFormData: builder.query({
      query: () => ({
        url: `${courses}`,
      }),
    }),
    getAllChaptersFormData: builder.query({
      query: () => ({
        url: `${chapters}`,
      }),
    }),
    getAllTopicsFormData: builder.query({
      query: () => ({
        url: `${topics}`,
      }),
    }),
    getContentViewData: builder.query({
      query: () => ({
        url: `${coursesContent}`,
      }),
    }),
    getStudentQuiz: builder.query({
      query: (val) => ({
        url: val.getPracticeQuiz
          ? `${students}/practiceQuiz/?chapterId=${val.courseId}`
          : `${students}/${quiz}?${courseid}=${val.courseId}`,
      }),
    }),
    getContentRelatedTopicData: builder.query({
      query: (id) => ({
        url: `${coursesContent}/${topic}/${id}`,
      }),
    }),
    getQuestionTypeData: builder.query({
      query: () => ({
        url: `${coursesContent}/${topic}`,
      }),
    }),
    getPracticeQuiz: builder.query({
      query: (val) => ({
        url: `${students}/practiceQuiz/?chapterId=${val.courseId}`,
      }),
    }),
    saveCourseData: builder.mutation({
      query: (params) => ({
        url: `${courses}`,
        method: "POST",
        body: params,
      }),
    }),
    saveChaptersData: builder.mutation({
      query: (params) => ({
        url: `${chapters}`,
        method: "POST",
        body: params,
      }),
    }),
    saveTopicsData: builder.mutation({
      query: (params) => ({
        url: `${topics}`,
        method: "POST",
        body: params,
      }),
    }),
    updateTopicsData: builder.mutation({
      query: (params) => ({
        url: `${topics}/${params.id}`,
        method: "PUT",
        body: params,
      }),
    }),
    getInnovationPodByStudentId: builder.query({
      query: (studentId) => ({
        url: `InnovationPod/student/${studentId}`,
        method: "GET",
      }),
    }),
    getSprintsByStageId: builder.query({
      query: (stageId) => ({
        url: `InnovationPod/stage/${stageId}/sprints`,
        method: "GET",
      }),
      providesTags: ["Sprint"],
    }),
    createInnovationPod: builder.mutation({
      query: (body) => ({
        url: `InnovationPod`,
        method: "POST",
        body,
      }),
    }),
    getInnovationPodById: builder.query({
      query: (id) => ({
        url: `InnovationPod/${id}`,
        method: "GET",
      }),
    }),
    getInnovationPods: builder.query({
      query: ({ page = 1, pageSize = 20 } = {}) => ({
        url: `InnovationPod`,
        method: "GET",
        params: { page, pageSize },
      }),
    }),
    getInnovationPodsByInstructorId: builder.query({
      query: (instructorId) => ({
        url: `InnovationPod/instructor/${instructorId}`,
        method: "GET",
      }),
    }),
    updateInnovationPod: builder.mutation({
      query: ({ podId, ...body }) => ({
        url: `InnovationPod/${podId}`,
        method: "PUT",
        body,
      }),
    }),
    deleteInnovationPod: builder.mutation({
      query: (podId) => ({
        url: `InnovationPod/${podId}`,
        method: "DELETE",
      }),
    }),
    validateSprint: builder.mutation({
      query: ({ sprintId, ...body }) => ({
        url: `InnovationPod/sprint/${sprintId}/validation`,
        method: "POST",
        body,
      }),
    }),
    updateSprintValidation: builder.mutation({
      query: ({ validationId, ...body }) => ({
        url: `InnovationPod/sprint/validation/${validationId}`,
        method: "PUT",
        body,
      }),
    }),
    completeStage: builder.mutation({
      query: (stageId) => ({
        url: `InnovationPod/stage/${stageId}/complete`,
        method: "PUT",
      }),
    }),
    sendPodMessage: builder.mutation({
      query: ({ podId, ...body }) => ({
        url: `InnovationPod/${podId}/message`,
        method: "POST",
        body,
      }),
    }),
    getPodChat: builder.query({
      query: ({ podId, page = 1, pageSize = 20 }) => ({
        url: `InnovationPod/${podId}/chat`,
        method: "GET",
        params: { page, pageSize },
      }),
    }),
    updatePodMessage: builder.mutation({
      query: ({ podId, messageId, ...body }) => ({
        url: `InnovationPod/${podId}/message/${messageId}`,
        method: "PUT",
        body,
      }),
    }),
    deletePodMessage: builder.mutation({
      query: ({ podId, messageId }) => ({
        url: `InnovationPod/${podId}/message/${messageId}`,
        method: "DELETE",
      }),
    }),
    submitSprintEvidence: builder.mutation({
      query: ({ sprintId, ...body }) => ({
        url: `InnovationPod/sprint/${sprintId}/evidence`,
        method: "POST",
        body,
      }),
    }),
    createSprint: builder.mutation({
      query: ({ stageId, ...body }) => ({
        url: `InnovationPod/stage/${stageId}/sprint`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Sprint"],
    }),
    updateSprint: builder.mutation({
      query: ({ sprintId, ...body }) => ({
        url: `InnovationPod/sprint/${sprintId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Sprint"],
    }),
    deleteSprint: builder.mutation({
      query: (sprintId) => ({
        url: `InnovationPod/sprint/${sprintId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Sprint"],
    }),
    assignStudentToPod: builder.mutation({
      query: ({ podId, studentId }) => ({
        url: `InnovationPod/${podId}/assign-student`,
        method: "POST",
        body: { studentId },
      }),
    }),
    removeStudentFromPod: builder.mutation({
      query: ({ podId, studentId }) => ({
        url: `InnovationPod/${podId}/remove-student/${studentId}`,
        method: "DELETE",
      }),
    }),
    updateInstructorInPod: builder.mutation({
      query: ({ podId, instructorId }) => ({
        url: `InnovationPod/${podId}/update-instructor`,
        method: "PUT",
        body: { instructorId },
      }),
    }),
    updateMentorInPod: builder.mutation({
      query: ({ podId, mentorId }) => ({
        url: `InnovationPod/${podId}/update-mentor`,
        method: "PUT",
        body: { mentorId },
      }),
    }),
    updateChaptersData: builder.mutation({
      query: (params) => ({
        url: `${chapters}/${params.id}`,
        method: "PUT",
        body: params,
      }),
    }),
    saveCoursesContent: builder.mutation({
      query: (params) => ({
        url: `${coursesContent}`,
        method: "POST",
        body: params,
      }),
    }),
    saveQuestionData: builder.mutation({
      query: (params) => ({
        url: `${questions}`,
        method: "POST",
        body: params,
      }),
    }),
    saveStudentQuiz: builder.mutation({
      query: (params) => ({
        url: `${students}/${quiz}/${params.quizId}/${submit}`,
        method: "PUT",
        body: { questionSet: params.quizzes, timeTaken: params.timeTaken },
      }),
    }),

    deleteCourseData: builder.mutation({
      query: (params) => ({
        url: `${courses}?id=${params.id}`,
        method: "DELETE",
      }),
    }),
    deleteChapterData: builder.mutation({
      query: (id) => ({
        url: `${chapters}?id=${id}`,
        method: "DELETE",
      }),
    }),
    deleteTopicData: builder.mutation({
      query: (id) => ({
        url: `${topics}?id=${id}`,
        method: "DELETE",
      }),
    }),
    getClassroomAndChapterAnalytics: builder.query({
      query: (params) => ({
        url: `Analytic/classroom/${params.classroomId}/chapter/${params.chapterId}`,
        method: "GET",
      }),
    }),
    // Add Questions management endpoints
    getAllQuestions: builder.query({
      query: (params) => ({
        url: `Questions?TopicId=${params.topicId}`,
        method: "GET",
      }),
    }),
    getQuestionsBySubtopicId: builder.query({
      query: (subtopicId) => ({
        url: `Questions/subtopic/${subtopicId}`,
        method: "GET",
      }),
    }),
    getQuestionById: builder.query({
      query: (params) => ({
        url: `Questions/${params.id}`,
        method: "GET",
      }),
    }),
    createQuestion: builder.mutation({
      query: (params) => ({
        url: "Questions",
        method: "POST",
        body: params,
      }),
    }),
    updateQuestion: builder.mutation({
      query: (params) => ({
        url: `Questions/${params.id}`,
        method: "PUT",
        body: params,
      }),
    }),
    deleteQuestion: builder.mutation({
      query: (id) => ({
        url: `Questions/${id}`,
        method: "DELETE",
      }),
    }),
    // Add Assignments management endpoints
    getAllAssignments: builder.query({
      query: (params) => ({
        url: `Assignments?SubtopicId=${params.subtopicId}`,
        method: "GET",
      }),
    }),
    // (removed duplicate getAssignmentById)
    createAssignment: builder.mutation({
      query: (params) => ({
        url: "Assignments",
        method: "POST",
        body: params,
      }),
    }),
    // New API: Create assignment by subtopic ID
    createAssignmentBySubtopic: builder.mutation({
      query: ({ subtopicId, ...body }) => ({
        url: `CoursesContent/${subtopicId}/assignments`,
        method: "POST",
        body: body,
      }),
    }),
    // New API: Update assignment weights by subtopic ID
    updateAssignmentWeights: builder.mutation({
      query: ({ subtopicId, weights }) => ({
        url: `CoursesContent/${subtopicId}/weight`,
        method: "PUT",
        body: { weights },
      }),
    }),
    updateAssignment: builder.mutation({
      query: (params) => ({
        url: `Assignments/${params.id}`,
        method: "PUT",
        body: params,
      }),
    }),
    updateAssignmentInCoursesContent: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `CoursesContent/assignments/${id}`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["Subtopic"],
    }),
    deleteAssignment: builder.mutation({
      query: (id) => ({
        url: `Assignments/${id}`,
        method: "DELETE",
      }),
    }),
    deleteAssignmentInCoursesContent: builder.mutation({
      query: (id) => ({
        url: `CoursesContent/assignments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Subtopic"],
    }),

    // Group Chat APIs
    getGroupChatHistory: builder.query({
      query: ({ groupId, page = 1, pageSize = 50 }) => ({
        url: `StudentGroup/${encodeURIComponent(groupId)}/chat?page=${page}&pageSize=${pageSize}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => [
        { type: "GroupChat", id: arg.groupId },
      ],
    }),
    sendGroupMessage: builder.mutation({
      query: ({ groupId, message, type = "TEXT" }) => ({
        url: `StudentGroup/${encodeURIComponent(groupId)}/message`,
        method: "POST",
        body: { Message: message, Type: type },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GroupChat", id: arg.groupId },
      ],
    }),
    editGroupMessage: builder.mutation({
      query: ({ groupId, messageId, message, type = "TEXT" }) => ({
        url: `StudentGroup/${encodeURIComponent(groupId)}/message/${encodeURIComponent(messageId)}`,
        method: "PUT",
        body: { Message: message, Type: type },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GroupChat", id: arg.groupId },
      ],
    }),
    deleteGroupMessage: builder.mutation({
      query: ({ groupId, messageId }) => ({
        url: `StudentGroup/${encodeURIComponent(groupId)}/message/${encodeURIComponent(messageId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GroupChat", id: arg.groupId },
      ],
    }),
    // // Existing endpoints...
    // getClassroomNotifications: builder.query({
    //   query: () => ({
    //     url: "Notifications/classroomNotification",
    //     method: "GET",
    //   }),
    // }),

    getAdminCourses: builder.query({
      query: () => ({
        url: "Courses",
        method: "GET",
      }),
    }),
    // Add these inside endpoints: (builder) => ({ ... })
    getSchoolClassrooms: builder.query({
      query: (schoolId) => ({
        url: `Schools/${schoolId}/classrooms`,
        method: "GET",
      }),
    }),

    getClassroomById: builder.query({
      query: (id) => ({
        url: `Classrooms/${id}`,
        method: "GET",
      }),
    }),

    createClassroom: builder.mutation({
      query: (params) => ({
        url: `Schools/${params.schoolId}/classrooms`,
        method: "POST",
        body: params,
      }),
    }),

    updateClassroom: builder.mutation({
      query: (params) => ({
        url: `Classrooms/${params.id}`,
        method: "PUT",
        body: params,
      }),
    }),

    deleteClassroom: builder.mutation({
      query: (id) => ({
        url: `Classrooms/${id}`,
        method: "DELETE",
      }),
    }),

    getUsers: builder.query({
      query: () => ({
        url: "/Users",
        method: "GET",
      }),
    }),

    getUserById: builder.query({
      query: (id) => ({
        url: `/Users/${id}`,
        method: "GET",
      }),
    }),

    createUser: builder.mutation({
      query: (body) => ({
        url: "/Users",
        method: "POST",
        body,
      }),
    }),

    updateUser: builder.mutation({
      query: (user) => ({
        url: `/Users`,
        method: "PATCH",
        body: user,
      }),
    }),

    deleteUser: builder.mutation({
      query: ({ Id }) => ({
        url: `/Users?Id=${Id}`,
        method: "DELETE",
      }),
    }),

    // Assignment scoring endpoint
    submitAssignmentScore: builder.mutation({
      query: (params) => ({
        url: "CourseContent/scoring",
        method: "PUT",
        body: params,
      }),
    }),

    // Classroom announcement endpoints
    getAnnouncements: builder.query({
      query: ({ type, id }) => ({
        url: `/Notifications/${type}/${id}`,
        method: "GET",
      }),
    }),
    createAnnouncement: builder.mutation({
      query: ({ type, classroomId, ...params }) => ({
        url: `/Notifications/${type}`,
        method: "POST",
        body: {
          classroomId,
          ...params,
        },
      }),
    }),

    // Classroom analytics endpoints
    // getClassroomAnalytics: builder.query({
    //   query: (classroomId) => ({
    //     url: `Classrooms/${classroomId}/analytics`,
    //     method: "GET",
    //   }),
    // }),
    getClassParticipation: builder.query({
      query: ({ classroomId, period }) => ({
        url: `Classrooms/${classroomId}/participation?period=${period}`,
        method: "GET",
      }),
    }),
    getClassPerformance: builder.query({
      query: ({ classroomId, period }) => ({
        url: `Classrooms/${classroomId}/performance?period=${period}`,
        method: "GET",
      }),
    }),

    // Student progress endpoints
    getStudentProgress: builder.query({
      query: ({ classroomId, studentId }) => ({
        url: `Classrooms/${classroomId}/students/${studentId}/progress`,
        method: "GET",
      }),
    }),
    removeStudentFromClassroom: builder.mutation({
      query: ({ classroomId, studentId }) => ({
        url: `Classrooms/${classroomId}/student/${studentId}`,
        method: "DELETE",
      }),
    }),

    // Topic management endpoints
    updateTopicEndDate: builder.mutation({
      query: ({ topicId, endDate }) => ({
        url: `Topics/${topicId}/endDate`,
        method: "PUT",
        body: { endDate },
      }),
    }),

    getSchoolCourses: builder.query({
      query: (schoolId) => ({
        url: `Schools/${schoolId}/courses`,
        method: "GET",
      }),
    }),

    addSchoolCourse: builder.mutation({
      query: (params) => ({
        url: `Schools/course`,
        method: "PUT",
        body: params,
      }),
    }),

    getSubjects: builder.query({
      query: () => ({
        url: "Subjects",
        method: "GET",
      }),
    }),

    getSubjectById: builder.query({
      query: (id) => ({
        url: `Subjects/${id}`,
        method: "GET",
      }),
    }),

    createSubject: builder.mutation({
      query: (params) => ({
        url: "Subjects",
        method: "POST",
        body: params,
      }),
    }),

    updateSubject: builder.mutation({
      query: (params) => ({
        url: `Subjects/${params.id}`,
        method: "PUT",
        body: params,
      }),
    }),

    deleteSubject: builder.mutation({
      query: (id) => ({
        url: `Subjects?id=${id}`,
        method: "DELETE",
      }),
    }),

    addQuestionToFinalOrInitialCourseQuiz: builder.mutation({
      query: (params) => ({
        url: `/SpecialQuizQuestions/addQuestion`,
        method: "PUT",
        body: params,
      }),
    }),
    removeQuestionFromFinalOrInitialCourseQuiz: builder.mutation({
      query: (params) => ({
        url: `/SpecialQuizQuestions/removeQuestion`,
        method: "PUT",
        body: params,
      }),
    }),

    listCourseInitailOrFinalQuizQuestions: builder.query({
      query: (params) => ({
        url: `/SpecialQuizQuestions/${params.courseId}/${
          params.isFinal === true ? "final" : "initial"
          }`,
        method: "GET",
      }),
    }),
    getStudentAnalytics: builder.query({
      query: ({ studentId, classroomId }) => ({
        url: `/Analytic/student/${studentId}/classroom/${classroomId}`,
        method: "GET",
      }),
    }),

    addStudentToClassroom: builder.mutation({
      query: ({ classroomId, studentId }) => ({
        url: `Classrooms/${classroomId}/student/${studentId}`,
        method: "PUT",
        // body: { studentId },
      }),
    }),
    addTeacherToClassroom: builder.mutation({
      query: ({ classroomId, teacherId }) => ({
        url: `Classrooms/${classroomId}/teacher/${teacherId}`,
        method: "PUT",
      }),
    }),
    // ... rest of the endpoints remain the same ...
    getStudentProjectDashboard: builder.query({
      query: () => ({
        url: `StudentGroup/student-dashboard`,
        method: "GET",
      }),
    }),

    // Project API endpoints
    getProjectById: builder.query({
      query: (projectId) => ({
        url: `Project/${projectId}`,
        method: "GET",
      }),
    }),

    getProjectAssignments: builder.query({
      query: (projectId) => ({
        url: `Project/${projectId}/assignments`,
        method: "GET",
      }),
    }),

    // Admin Project Management API endpoints
    getAllProjects: builder.query({
      query: (type = "ALL") => ({
        url: `Project?Type=${type}`,
        method: "GET",
      }),
    }),

    createProject: builder.mutation({
      query: (projectData) => ({
        url: `Project`,
        method: "POST",
        body: projectData,
      }),
    }),

    updateProject: builder.mutation({
      query: ({ projectId, projectData }) => ({
        url: `Project/${projectId}`,
        method: "PUT",
        body: projectData,
      }),
    }),

    updateProjectStatus: builder.mutation({
      query: ({ projectId, status }) => ({
        url: `Project/${projectId}/status`,
        method: "PUT",
        body: { status },
      }),
    }),

    deleteProject: builder.mutation({
      query: (projectId) => ({
        url: `Project/${projectId}`,
        method: "DELETE",
      }),
    }),

    // Industry Project API endpoints
    getProjectWaitingList: builder.query({
      query: (projectId) => ({
        url: `Project/${projectId}/waiting-list`,
        method: "GET",
      }),
    }),

    generateProjectGroups: builder.mutation({
      query: (projectId) => ({
        url: `Project/${projectId}/generate-group`,
        method: "POST",
      }),
    }),

    requestJoinProject: builder.mutation({
      query: (projectId) => ({
        url: `Project/${projectId}/join-request`,
        method: "PUT",
      }),
    }),
    createStudentFeedback: builder.mutation({
      query: (params) => ({
        url: "StudentFeedbacks",
        method: "POST",
        body: params,
      }),
    }),
    getStudentFeedbacks: builder.query({
      query: ({ studentId, page = 1, size = 20 }) => ({
        url: `StudentFeedbacks/student/${studentId}`,
        params: { page, size },
        method: "GET",
      }),
    }),

    // Badge API endpoints
    getMyBadge: builder.query({
      query: () => ({
        url: "Badge",
        method: "GET",
      }),
    }),
    addSubmissionToBadge: builder.mutation({
      query: (params) => ({
        url: "Badge",
        method: "POST",
        body: params,
      }),
    }),
    removeBadgeItem: builder.mutation({
      query: (badgeId) => ({
        url: `Badge/${badgeId}`,
        method: "DELETE",
      }),
    }),

    cancelJoinRequest: builder.mutation({
      query: (projectId) => ({
        url: `Project/${projectId}/cancel-join-request`,
        method: "PUT",
      }),
    }),

    updateJoinRequest: builder.mutation({
      query: ({ projectId, studentId, statusUpdate }) => ({
        url: `Project/${projectId}/update-join-request`,
        method: "PUT",
        body: { StudentId: studentId, StatusUpdate: statusUpdate },
      }),
    }),

    // Project Assignment API endpoints
    createProjectAssignment: builder.mutation({
      query: ({ projectId, assignmentData }) => ({
        url: `Project/${projectId}/assignment`,
        method: "POST",
        body: assignmentData,
      }),
    }),

    updateProjectAssignment: builder.mutation({
      query: ({ projectId, assignmentId, assignmentData }) => ({
        url: `Project/${projectId}/assignment/${assignmentId}`,
        method: "PUT",
        body: assignmentData,
      }),
    }),

    deleteProjectAssignment: builder.mutation({
      query: ({ projectId, assignmentId }) => ({
        url: `Project/${projectId}/assignment/${assignmentId}`,
        method: "POST",
      }),
    }),

    // Project Group API endpoints
    createAcademicGroup: builder.mutation({
      query: (groupData) => ({
        url: `StudentGroup/academic-group`,
        method: "POST",
        body: groupData,
      }),
    }),

    addStudentToGroup: builder.mutation({
      query: ({ groupId, studentIds }) => ({
        url: `StudentGroup/${groupId}/addStudent`,
        method: "PUT",
        body: { StudentIds: studentIds },
      }),
    }),

    removeStudentFromGroup: builder.mutation({
      query: ({ groupId, studentIds }) => ({
        url: `StudentGroup/${groupId}/removeStudent`,
        method: "PUT",
        body: { StudentIds: studentIds },
      }),
    }),

    moveStudentsToGroup: builder.mutation({
      query: ({ groupId, studentIds, destinationGroupId }) => ({
        url: `StudentGroup/${groupId}/moveStudents`,
        method: "PUT",
        body: {
          StudentIds: studentIds,
          DestinationGroupId: destinationGroupId,
        },
      }),
    }),

    updateGroupInstructor: builder.mutation({
      query: ({ groupId, instructorId }) => ({
        url: `StudentGroup/${groupId}/updateInstructor`,
        method: "PUT",
        body: { InstructorId: instructorId },
      }),
    }),

    updateGroupLeader: builder.mutation({
      query: ({ groupId, leaderId }) => ({
        url: `StudentGroup/${groupId}/updateLeader`,
        method: "PUT",
        body: { LeaderId: leaderId },
      }),
    }),

    updateGroup: builder.mutation({
      query: ({ groupId, groupData }) => ({
        url: `StudentGroup/${groupId}`,
        method: "PUT",
        body: groupData,
      }),
    }),

    deleteGroup: builder.mutation({
      query: (groupId) => ({
        url: `StudentGroup/${groupId}`,
        method: "DELETE",
      }),
    }),

    // Project Submission API endpoints
    editAssignmentSubmission: builder.mutation({
      query: ({ groupId, assignmentId, fileUrl }) => ({
        url: `StudentGroup/${groupId}/submission/${assignmentId}`,
        method: "PUT",
        body: { FileUrl: fileUrl },
      }),
    }),

    peerReviewAssignment: builder.mutation({
      query: ({ groupId, assignmentId }) => ({
        url: `StudentGroup/${groupId}/submission/${assignmentId}/peer-review`,
        method: "PUT",
      }),
    }),

    scoreAssignment: builder.mutation({
      query: ({ groupId, assignmentId, score }) => ({
        url: `StudentGroup/${groupId}/submission/${assignmentId}/score`,
        method: "PUT",
        body: { Score: score },
      }),
    }),

    // Group API endpoints
    getGroupById: builder.query({
      query: (groupId) => ({
        url: `StudentGroup/${groupId}`,
        method: "GET",
      }),
    }),

    getGroupsByStudentId: builder.query({
      query: (studentId) => ({
        url: `StudentGroup/student/${studentId}`,
        method: "GET",
      }),
    }),

    getGroupsByProjectId: builder.query({
      query: (projectId) => ({
        url: `StudentGroup/project/${projectId}`,
        method: "GET",
      }),
    }),

    getGroupsByInstructorId: builder.query({
      query: (instructorId) => ({
        url: `StudentGroup/instructor/${encodeURIComponent(instructorId)}`,
        method: "GET",
      }),
    }),

    // Chat API endpoints
    getGroupChatHistory: builder.query({
      query: ({ groupId, page = 1, pageSize = 20 }) => ({
        url: `StudentGroup/${groupId}/chat?page=${page}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),

    sendGroupMessage: builder.mutation({
      query: ({ groupId, message, type = "TEXT" }) => ({
        url: `StudentGroup/${groupId}/message`,
        method: "POST",
        body: { Message: message, Type: type },
      }),
    }),

    editGroupMessage: builder.mutation({
      query: ({ groupId, messageId, message, type = "TEXT" }) => ({
        url: `StudentGroup/${groupId}/message/${messageId}`,
        method: "PUT",
        body: { Message: message, Type: type },
      }),
    }),

    deleteGroupMessage: builder.mutation({
      query: ({ groupId, messageId }) => ({
        url: `StudentGroup/${groupId}/message/${messageId}`,
        method: "DELETE",
      }),
    }),

    // Submission API endpoints
    getAssignmentSubmission: builder.query({
      query: ({ groupId, assignmentId }) => ({
        url: `StudentGroup/${groupId}/assignment/${assignmentId}`,
        method: "GET",
      }),
    }),

    submitAssignment: builder.mutation({
      query: ({ groupId, assignmentId, fileUrl }) => ({
        url: `StudentGroup/${groupId}/submission/${assignmentId}`,
        method: "POST",
        body: { FileUrl: fileUrl },
      }),
    }),

    // User API endpoints
    getUserById: builder.query({
      query: (userId) => ({
        url: `Users/${userId}`,
        method: "GET",
      }),
    }),

    // Expert API endpoints
    getExperts: builder.query({
      query: () => ({
        url: "UserProfiles/expert",
        method: "GET",
      }),
    }),
    getCalendarEvents: builder.query({
      query: ({ start, end }) => ({
        url: `event/calendar?start=${start}&end=${end}`,
        method: "GET",
      }),
    }),
    // Events API
    getEventById: builder.query({
      query: (eventId) => ({
        url: `event/${eventId}`,
        method: "GET",
      }),
    }),
    getMyCreatedEvents: builder.query({
      query: () => ({
        url: `event/my-created-events`,
        method: "GET",
      }),
    }),
    getMyJoinedEvents: builder.query({
      query: () => ({
        url: `event/my-joined-events`,
        method: "GET",
      }),
    }),
    getEventsByClassroomId: builder.query({
      query: (classroomId) => ({
        url: `event/classroom/${classroomId}`,
        method: "GET",
      }),
    }),
    getEventsByType: builder.query({
      query: (eventType) => ({
        url: `event/type/${eventType}`,
        method: "GET",
      }),
    }),
    getEventsByRecurringId: builder.query({
      query: (recurringId) => ({
        url: `event/recurring/${recurringId}`,
        method: "GET",
      }),
    }),
    createClassroomEvent: builder.mutation({
      query: ({
        ClassroomId,
        Description,
        StartTime,
        EndTime,
        RecurringEventId,
      }) => ({
        url: "event/classroom-event",
        method: "POST",
        body: {
          ClassroomId,
          Description,
          StartTime,
          EndTime,
          RecurringEventId,
        },
      }),
    }),
    updateEvent: builder.mutation({
      query: ({ eventId, ...body }) => ({
        url: `event/${eventId}`,
        method: "PUT",
        body,
      }),
    }),
    updateEventRecurringId: builder.mutation({
      query: ({ eventId, recurringId }) => ({
        url: `event/${eventId}/recurring/${recurringId}`,
        method: "PUT",
      }),
    }),
    deleteEvent: builder.mutation({
      query: (eventId) => ({
        url: `event/${eventId}`,
        method: "DELETE",
      }),
    }),
    createRecurringEvent: builder.mutation({
      query: (body) => ({
        url: "event/recurring-event",
        method: "POST",
        body,
      }),
    }),
    createRecurringFromBaseEvent: builder.mutation({
      query: (body) => ({
        url: "event/recurring-from-base-event",
        method: "POST",
        body,
      }),
    }),
    updateRecurringEvents: builder.mutation({
      query: ({ recurringId, ...body }) => ({
        url: `event/recurring-events/${recurringId}`,
        method: "PUT",
        body,
      }),
    }),
    deleteRecurringEvents: builder.mutation({
      query: (recurringId) => ({
        url: `event/recurring-events/${recurringId}`,
        method: "DELETE",
      }),
    }),
    getOneOneSessions: builder.query({
      query: (classroomId) => ({
        url: classroomId
          ? `event/classroom/${classroomId}`
          : "event/one-one-session",
        method: "GET",
      }),
    }),
    createOneOneSession: builder.mutation({
      query: (body) => ({
        url: "event/one-one-session",
        method: "POST",
        body,
      }),
    }),
    updateOneOneSession: builder.mutation({
      query: ({ eventId, ...body }) => ({
        url: `event/${eventId}`,
        method: "PUT",
        body,
      }),
    }),
    deleteOneOneSession: builder.mutation({
      query: (eventId) => ({
        url: `event/${eventId}`,
        method: "DELETE",
      }),
    }),
    createQuiz: builder.mutation({
      query: (params) => ({
        url: "Quizzes",
        method: "POST",
        body: params,
      }),
    }),
    sendEmail: builder.mutation({
      query: (body) => ({
        url: "Notifications/sendEmail",
        method: "POST",
        body,
      }),
    }),
    sendWhatsapp: builder.mutation({
      query: (body) => ({
        url: "Notifications/sendWhatsapp",
        method: "POST",
        body,
      }),
    }),
    requestWhatsappApiKey: builder.mutation({
      query: (body) => ({
        url: "Notifications/requestWhatsappApiKey",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const liveKitApi = createApi({
  reducerPath: "liveKitApi",
  baseQuery: liveKitBaseQuery,
  endpoints: (builder) => ({
    getClassRoomToken: builder.query({
      query: ({ classroomId, displayName }) =>
        `token/get_class_room_token?classroom_id=${classroomId}&display_name=${displayName}`,
    }),
    getBreakoutRoomToken: builder.query({
      query: ({ classroomId, displayName, breakoutRoomId }) =>
        `token/get_breakout_room_token?classroom_id=${classroomId}&display_name=${displayName}&breakout_room_id=${breakoutRoomId}`,
    }),
    getBreakoutRoomList: builder.query({
      query: (classroomId) =>
        `room/breakout_room_list?classroom_id=${classroomId}`,
    }),
    // Recording room token for guest
    getRecordingRoomTokenForGuest: builder.query({
      query: ({ recording_request_id, display_name, identity }) => {
        let url = `token/get_recording_room_token_for_guest?recording_request_id=${encodeURIComponent(recording_request_id)}`;
        if (display_name) {
          url += `&display_name=${encodeURIComponent(display_name)}`;
        }
        if (identity) {
          url += `&identity=${encodeURIComponent(identity)}`;
        }
        return url;
      },
    }),

    // Generate recording room token for guest (admin use)
    generateRecordingRoomTokenForGuest: builder.mutation({
      query: ({ recording_request_id }) => ({
        url: `token/generate_recording_room_token_for_guest/${encodeURIComponent(recording_request_id)}`,
        method: "PUT",
      }),
    }),
    // Recording room token for instructor
    getRecordingRoomToken: builder.query({
      query: ({ recording_request_id, display_name }) =>
        `token/get_recording_room_token?recording_request_id=${encodeURIComponent(recording_request_id)}&display_name=${encodeURIComponent(display_name)}`,
    }),
    createBreakoutRoom: builder.mutation({
      query: ({ classroomId, breakoutRoomName }) => ({
        url: "room/create_breakout_room",
        method: "POST",
        body: {
          classroom_id: classroomId,
          breakout_room_name: breakoutRoomName,
        },
      }),
    }),
    deleteBreakoutRoom: builder.mutation({
      query: ({ classroomId, breakoutRoomId }) => ({
        // API expects breakout_room_name in query
        url: `room/delete_breakout_room?classroom_id=${classroomId}&breakout_room_name=${breakoutRoomId}`,
        method: "DELETE",
      }),
    }),
    // Recording Requests APIs
    createRecordingRequest: builder.mutation({
      query: (body) => ({
        url: `recording_request/`,
        method: "POST",
        body,
      }),
    }),
    listRecordingRequests: builder.query({
      query: (params) => ({
        url: `recording_request/`,
        params,
      }),
    }),
    listInstructorRecordingRequests: builder.query({
      query: (params) => ({
        url: `recording_request/instructor_request`,
        params,
      }),
    }),
    getRecordingRequestById: builder.query({
      query: (id) => ({
        url: `recording_request/${id}`,
      }),
    }),
    updateRecordingRequestStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `recording_request/${id}/status`,
        method: "PUT",
        body: { status },
      }),
    }),
    updateRecordingRequestDetails: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `recording_request/${id}`,
        method: "PUT",
        body,
      }),
    }),
    deleteRecordingRequest: builder.mutation({
      query: (id) => ({
        url: `recording_request/${id}`,
        method: "DELETE",
      }),
    }),
    // Egress control
    startPresenterEgress: builder.mutation({
      query: (body) => ({
        url: `room/start_presenter_egress`,
        method: "POST",
        body,
      }),
    }),
    startScreenshareEgress: builder.mutation({
      query: (body) => ({
        url: `room/start_screenshare_egress`,
        method: "POST",
        body,
      }),
    }),
    startRoomCompositeEgress: builder.mutation({
      query: (body) => ({
        url: `room/start_room_composite_egress`,
        method: "POST",
        body,
      }),
    }),
    stopEgress: builder.mutation({
      query: (body) => ({
        url: `room/stop_egress`,
        method: "POST",
        body,
      }),
    }),
    saveTestRecordingUrl: builder.mutation({
      query: ({ recording_request_id, test_recording_url }) => ({
        url: `recording_request/${encodeURIComponent(recording_request_id)}/test_recording_url?test_recording_url=${encodeURIComponent(test_recording_url)}`,
        method: "PUT",
      }),
    }),
    getTestRecordingStatus: builder.query({
      query: (recording_request_id) => ({
        url: `recording_request/${encodeURIComponent(recording_request_id)}/test_recording_status`,
      }),
    }),
    confirmTestRecording: builder.mutation({
      query: (recording_request_id) => ({
        url: `recording_request/${encodeURIComponent(recording_request_id)}/confirm_test_recording`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  // Add content management exports
  useGetAllContentQuery,
  useGetContentByIdQuery,
  useGetContentByTopicIdQuery,
  useLazyGetContentByIdQuery,
  useLazyGetContentByTopicIdQuery,
  useCreateContentMutation,
  useUpdateContentMutation,
  useDeleteContentMutation,
  // Add Questions management exports
  useLazyGetAllQuestionsQuery,
  useGetAllQuestionsQuery,
  useGetQuestionsBySubtopicIdQuery,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  // Add Assignments management exports
  useGetAllAssignmentsQuery,
  useGetAssignmentByIdQuery,
  useCreateAssignmentMutation,
  useCreateAssignmentBySubtopicMutation,
  useUpdateAssignmentWeightsMutation,
  useUpdateAssignmentMutation,
  useUpdateAssignmentInCoursesContentMutation,
  useDeleteAssignmentMutation,
  useDeleteAssignmentInCoursesContentMutation,
  useLazyGetAdminCoursesQuery,
  useGetQuestionByIdQuery,
  // Existing exports...
  useLazyGetTestDataQuery,
  useLazyGetAllCourseDataQuery,
  useLazyGetStudentPerformanceDataQuery,
  useLazyGetCourseDetailsDataQuery,
  useLazyGetAllSubjectsDataQuery,
  useGetAllCourseDataQuery,
  useLazyGetAllCoursesFormDataQuery,
  useLazyGetAllChaptersFormDataQuery,
  useLazyGetAllTopicsFormDataQuery,
  useLazyGetStudentQuizQuery,
  useLazyGetContentViewDataQuery,
  useLazyGetContentRelatedTopicDataQuery,
  useLazyGetBookmarksQuery,
  useGetBookmarksQuery,
  useCreateBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarkCollectionQuery,
  useCreateBookmarkCollectionMutation,
  // Submissions API hooks
  useGetSubmissionByIdQuery,
  useGetClassSubmissionsQuery,
  useLazyGetClassSubmissionsQuery,
  useLazyGetStudentSubmissionQuery,
  useCreateSubmissionMutation,
  useUpdateSubmissionMutation,
  useGetCourseAssignmentsSubmissionsQuery,
  useScoreClassSubmissionMutation,
  useGetClassroomNotificationsQuery,
  useLazyGetClassroomNotificationsQuery,
  useLazyGetBookmarkCollectionQuery,
  useEditBookmarkCollectionMutation,
  useDeleteBookmarkCollectionMutation,
  useLazyGetQuestionTypeDataQuery,
  useLazyGetTopicQuizPerformanceQuery,
  useSaveCourseDataMutation,
  useSaveChaptersDataMutation,
  useSaveTopicsDataMutation,
  useUpdateTopicsDataMutation,
  useUpdateChaptersDataMutation,
  useSaveCoursesContentMutation,
  useSaveQuestionDataMutation,
  useSaveStudentQuizMutation,
  useDeleteCourseDataMutation,
  useDeleteChapterDataMutation,
  useDeleteTopicDataMutation,
  useLazyGetQuizQuestionsQuery,
  useGetSubtopicContentQuery,
  useLazyGetSubtopicContentQuery,
  useLazyGetQuestionByIdQuery,
  useLazyGetSchoolClassroomsQuery,
  useGetStudentAllNotesQuery,
  useGetStudentNoteByIdQuery,
  useGetStudentNotesByCourseQuery,
  useGetStudentNotesByTopicQuery,
  useCreateNewStudentNoteMutation,
  useUpdateStudentNoteMutation,
  useDeleteStudentNoteMutation,
  useGetTopicQuizPerformanceQuery,
  useGetPracticeQuizQuery,
  useLazyGetPracticeQuizQuery,
  useGetAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useGetSchoolNotificationsQuery,
  useGetStudentNotificationsQuery,
  useCreateSchoolNotificationMutation,
  useCreateClassroomNotificationMutation,
  useUpdateStudentNotificationMutation,
  useLazyGetAnnouncementsQuery,
  useLazyGetSchoolNotificationsQuery,
  useLazyGetStudentNotificationsQuery,
  useGetQueriesQuery,
  useCreateQueryMutation,
  useGetAdminCoursesQuery,
  useGetCourseByIdQuery,
  useLazyGetCourseByIdQuery,
  useGetCourseProgressQuery,
  useLazyGetCourseProgressQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useAssignInstructorMutation,
  useAssignExpertMutation,
  useGetSubjectsQuery,
  useGetSchoolsQuery,
  useGetSchoolByIdQuery,
  useCreateSchoolMutation,
  useUpdateSchoolMutation,
  useDeleteSchoolMutation,
  useGetSchoolClassroomsQuery,
  useGetClassroomByIdQuery,
  useCreateClassroomMutation,
  useUpdateClassroomMutation,
  useGetSchoolCoursesQuery,
  useAddSchoolCourseMutation,
  useDeleteClassroomMutation,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useUpdateQueryMutation,
  useUpdateReplyMutation,
  useGetClassroomAnalyticsQuery,
  useGetClassParticipationQuery,
  useGetClassPerformanceQuery,
  useGetStudentProgressQuery,
  useAddStudentToClassroomMutation,
  useAddTeacherToClassroomMutation,
  useRemoveStudentFromClassroomMutation,
  useUpdateTopicEndDateMutation,
  useGetClassroomAndChapterAnalyticsQuery,
  useGetSubjectByIdQuery,
  useCreateSubjectMutation,
  useUpdateSubjectMutation,
  useDeleteSubjectMutation,
  useListCourseInitailOrFinalQuizQuestionsQuery,
  useAddQuestionToFinalOrInitialCourseQuizMutation,
  useRemoveQuestionFromFinalOrInitialCourseQuizMutation,
  useLazyGetClassroomAnalyticsQuery,
  useGetStudentAnalyticsQuery,
  useLazyGetTopicsQuery,
  useGetSubtopicsQuery,
  useLazyGetSubtopicsQuery,
  useGetSubtopicByIdQuery,
  useCreateSubtopicMutation,
  useUpdateSubtopicMutation,
  useDeleteSubtopicMutation,
  useUpdateSubtopicWeightsMutation,
  useReorderSubtopicsMutation,
  useUploadScormFileMutation,
  useSubmitAssignmentScoreMutation,
  useGetAllClassSubmissionsQuery,
  useLazyGetAllClassSubmissionsQuery,
  useGetStudentDashboardQuery,
  useLazyGetStudentDashboardQuery,
  useLazyGetStudentAllNotesQuery,
  useLazyGetStudentNoteByIdQuery,
  useLazyGetStudentNotesByCourseQuery,
  useLazyGetStudentNotesByTopicQuery,
  useGetStudentProjectDashboardQuery,
  useLazyGetStudentProjectDashboardQuery,
  // Project API hooks
  useGetProjectByIdQuery,
  useLazyGetProjectByIdQuery,
  useGetProjectAssignmentsQuery,
  useEvaluateSubmissionAIMutation,
  useLazyGetProjectAssignmentsQuery,
  useLazyGetAssignmentByIdQuery,
  // Admin Project Management API hooks
  useGetAllProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectStatusMutation,
  // Industry Project API hooks
  useGetProjectWaitingListQuery,
  useGenerateProjectGroupsMutation,
  useRequestJoinProjectMutation,
  useCancelJoinRequestMutation,
  useUpdateJoinRequestMutation,
  // Project Assignment API hooks
  useCreateProjectAssignmentMutation,
  useUpdateProjectAssignmentMutation,
  useDeleteProjectAssignmentMutation,
  // Project Group API hooks
  useCreateAcademicGroupMutation,
  useAddStudentToGroupMutation,
  useRemoveStudentFromGroupMutation,
  useMoveStudentsToGroupMutation,
  useUpdateGroupInstructorMutation,
  useUpdateGroupLeaderMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  // Project Submission API hooks
  useEditAssignmentSubmissionMutation,
  usePeerReviewAssignmentMutation,
  useScoreAssignmentMutation,
  // Group API hooks
  useGetGroupByIdQuery,
  useLazyGetGroupByIdQuery,
  useGetGroupsByStudentIdQuery,
  useLazyGetGroupsByStudentIdQuery,
  useGetGroupsByProjectIdQuery,
  useLazyGetGroupsByProjectIdQuery,
  useGetGroupsByInstructorIdQuery,
  useLazyGetGroupsByInstructorIdQuery,
  // Chat API hooks
  useGetGroupChatHistoryQuery,
  useLazyGetGroupChatHistoryQuery,
  useSendGroupMessageMutation,
  useEditGroupMessageMutation,
  useDeleteGroupMessageMutation,
  // Submission API hooks
  useGetAssignmentSubmissionQuery,
  useLazyGetAssignmentSubmissionQuery,
  useSubmitAssignmentMutation,
  // User API hooks (useGetUserByIdQuery already exported above)
  useLazyGetUserByIdQuery,
  useGetEventByIdQuery,
  useGetMyCreatedEventsQuery,
  useGetMyJoinedEventsQuery,
  useGetEventsByClassroomIdQuery,
  useGetEventsByTypeQuery,
  useGetEventsByRecurringIdQuery,
  useCreateClassroomEventMutation,
  useUpdateEventMutation,
  useUpdateEventRecurringIdMutation,
  useDeleteEventMutation,
  useCreateRecurringEventMutation,
  useCreateRecurringFromBaseEventMutation,
  useUpdateRecurringEventsMutation,
  useDeleteRecurringEventsMutation,
  useGetCalendarEventsQuery,
  // Expert API hooks
  useGetExpertsQuery,
  useGetOneOneSessionsQuery,
  useCreateOneOneSessionMutation,
  useUpdateOneOneSessionMutation,
  useDeleteOneOneSessionMutation,
  useCreateQuizMutation,
  useGetInnovationPodByStudentIdQuery,
  useLazyGetInnovationPodByStudentIdQuery,
  useGetSprintsByStageIdQuery,
  useLazyGetSprintsByStageIdQuery,
  useCreateInnovationPodMutation,
  useSubmitSprintEvidenceMutation,
  useGetInnovationPodByIdQuery,
  useLazyGetInnovationPodByIdQuery,
  useCreateSprintMutation,
  useAssignStudentToPodMutation,
  useGetInnovationPodsQuery,
  useLazyGetInnovationPodsQuery,
  useGetInnovationPodsByInstructorIdQuery,
  useLazyGetInnovationPodsByInstructorIdQuery,
  useValidateSprintMutation,
  useUpdateSprintValidationMutation,
  useCompleteStageMutation,
  useSendPodMessageMutation,
  useGetPodChatQuery,
  useLazyGetPodChatQuery,
  useUpdatePodMessageMutation,
  useDeletePodMessageMutation,
  useUpdateInnovationPodMutation,
  useDeleteInnovationPodMutation,
  useRemoveStudentFromPodMutation,
  useUpdateInstructorInPodMutation,
  useUpdateMentorInPodMutation,
  useUpdateSprintMutation,
  useDeleteSprintMutation,
  useGetStudentLearningIndicesQuery,
  useGetStudentLearningIndicesProgressQuery,
  useGetStudentSkillTagAnalyticsQuery,
  useGetStudentAssessmentPerformanceQuery,
  useCheckPlagiarismMutation,
  useGetPlagiarismResultQuery,
  useSendEmailMutation,
  useSendWhatsappMutation,
  useRequestWhatsappApiKeyMutation,
  useCreateStudentFeedbackMutation,
  useGetStudentFeedbacksQuery,
  // Badge API hooks
  useGetMyBadgeQuery,
  useAddSubmissionToBadgeMutation,
  useRemoveBadgeItemMutation,
} = APIConnect;
export const {
  useGetClassRoomTokenQuery,
  useGetBreakoutRoomTokenQuery,
  useGetBreakoutRoomListQuery,
  useCreateBreakoutRoomMutation,
  useDeleteBreakoutRoomMutation,
  // Recording Requests hooks
  useCreateRecordingRequestMutation,
  useListRecordingRequestsQuery,
  useListInstructorRecordingRequestsQuery,
  useGetRecordingRequestByIdQuery,
  useLazyGetRecordingRequestByIdQuery,
  useUpdateRecordingRequestStatusMutation,
  useUpdateRecordingRequestDetailsMutation,
  useDeleteRecordingRequestMutation,
  useStartPresenterEgressMutation,
  useStartScreenshareEgressMutation,
  useStopEgressMutation,
  useGetRecordingRoomTokenForGuestQuery,
  useLazyGetRecordingRoomTokenForGuestQuery,
  useGenerateRecordingRoomTokenForGuestMutation,
  useGetRecordingRoomTokenQuery,
  useLazyGetRecordingRoomTokenQuery,
  useStartRoomCompositeEgressMutation,
  useSaveTestRecordingUrlMutation,
  useGetTestRecordingStatusQuery,
  useLazyGetTestRecordingStatusQuery,
  useConfirmTestRecordingMutation,
} = liveKitApi;
export default APIConnect;
